import styled from "styled-components";
import { theme } from "../theme";

export const MessageComposerContainer = styled.div`
  /* THE EMOJI PICKER'S ANCHOR.
     The picker is position: absolute with bottom: 100%, so it needs a
     positioned ancestor to sit above the composer. This container was static
     on desktop — only the mobile media query below set position: sticky — so
     the picker escaped to whatever distant ancestor happened to be positioned
     and opened somewhere off-screen. The button worked the whole time; the
     panel it opened was simply nowhere the user could see it. */
  position: relative;
  padding: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
  background: ${theme.colors.background.primary};
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    position: sticky;
    bottom: 0;
    z-index: 10;
    background: ${theme.colors.background.primary};
    backdrop-filter: blur(10px);
    border-top: 1px solid ${theme.colors.border};
  }
`;

export const MessageComposerForm = styled.form`
  display: flex;
  align-items: flex-end;
  gap: ${theme.spacing.sm};
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.xl};
    gap: ${theme.spacing.md};
  }
`;

export const MessageInputContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const MessageTextarea = styled.textarea`
  width: 100%;
  min-height: 40px;
  max-height: 120px;
  border: none;
  background: transparent;
  color: ${theme.colors.text.primary};
  font-size: 1rem; /* Prevent iOS zoom on focus */
  line-height: 1.4;
  resize: none;
  outline: none;
  
  &::placeholder {
    color: ${theme.colors.text.secondary};
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    min-height: 48px;
    max-height: 100px;
    font-size: 1rem; /* Prevent zoom on iOS */
    line-height: 1.5;
  }
`;

export const MessageComposerActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.xs};
`;

export const ComposerActionButton = styled.button`
  padding: ${theme.spacing.xs};
  border: none;
  background: transparent;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.sm};
  transition: all ${theme.transitions.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

export const AttachmentButton = styled(ComposerActionButton)`
  &:hover {
    color: ${theme.colors.primary};
  }
`;

export const EmojiButton = styled(ComposerActionButton)`
  &:hover {
    color: ${theme.colors.warning};
  }
`;

export const SendButton = styled.button<{ $disabled?: boolean }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: none;
  background: ${props => props.$disabled 
    ? theme.colors.text.secondary 
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${theme.colors.text.light};
  border-radius: ${theme.borderRadius.md};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  font-weight: 600;
  transition: all ${theme.transitions.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.medium};
  }
  
  &:disabled {
    opacity: 0.6;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.lg};
    min-width: 48px;
    min-height: 48px;
    
    &:active:not(:disabled) {
      transform: scale(0.95);
    }
  }
`;

export const AttachmentPreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.sm};
`;

export const AttachmentPreviewInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const AttachmentPreviewName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AttachmentPreviewSize = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
`;

export const AttachmentPreviewRemove = styled.button`
  padding: ${theme.spacing.xs};
  border: none;
  background: transparent;
  color: ${theme.colors.error};
  cursor: pointer;
  border-radius: ${theme.borderRadius.sm};
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: ${theme.colors.error};
    color: ${theme.colors.text.light};
  }
`;

export const EmojiPicker = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.heavy};
  padding: ${theme.spacing.sm};
  z-index: 1000;
  width: max-content;
  max-width: min(300px, calc(100vw - ${theme.spacing.lg}));
`;

export const EmojiGrid = styled.div`
  display: grid;
  /* minmax(0, 1fr), not 1fr. A grid item's default min-width is auto, so an
     emoji plus its padding could force each of the eight columns wider than
     its share and push the grid past the picker's 300px — spilling the last
     column off the right of the screen, where it could be seen but not
     clicked. minmax(0, ...) lets the columns actually shrink. */
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: ${theme.spacing.xs};
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const EmojiItem = styled.button`
  padding: ${theme.spacing.xs};
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: ${theme.borderRadius.sm};
  transition: all ${theme.transitions.normal};
  font-size: 1.25rem;
  /* Same reason as the columns above: a button will not shrink below its
     content unless told it may. */
  min-width: 0;
  line-height: 1;
  
  &:hover {
    background: ${theme.colors.background.secondary};
  }
`;

export const CharacterCount = styled.div<{ $isOverLimit: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.$isOverLimit ? theme.colors.error : theme.colors.text.secondary};
  text-align: right;
  margin-top: ${theme.spacing.xs};
`;

export const MessageComposerError = styled.div`
  color: ${theme.colors.error};
  font-size: 0.75rem;
  margin-top: ${theme.spacing.xs};
  text-align: center;
`;

export const UploadProgress = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.sm};
`;

export const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background: ${theme.colors.background.secondary};
  border-radius: 2px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  width: ${props => props.$progress}%;
  transition: width ${theme.transitions.normal};
`;

export const ProgressText = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  min-width: 40px;
  text-align: right;
`;
